import Image from "./Image";
import { useInshurLanding } from "../landingMode";
import { LINKS } from "../homeLinks";
import { Lock, Search } from "lucide-react";
/* Second, dark-theme "enterprise" section. Reuses the exact eyebrow, title and subcopy of
   RentidEnterpriseSection, but renders on a near-black cinematic hero panel with a whisper
   of charcoal topography and one polished layered artwork: a dominant center "Rental Brand"
   commerce app, with a Driver Verifications API window tucked behind-left and a Commercial
   Insurance API terminal overlapping in front-right. Understated annotation labels and clean
   circular Rental Brand + RentShield badges complete the composition. */
// Each API window uses its own brand accent (defined locally in the window): rentID blue for
// the driver-verifications response, rentShield coral for the policies response.
// Understated floating annotation (not a button): small, low-contrast, hollow dot + text.
function FloatingLabel({ label, className = "" }) {
    return (<span className={`inline-flex items-center gap-2.5 whitespace-nowrap rounded-xl border border-white/25 bg-white/15 px-6 py-3.5 text-[12px] font-medium text-white shadow-md shadow-black/30 backdrop-blur-xl ${className}`}>
      <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-full border border-white/60"/>
      {label}
    </span>);
}
// Shared window shell — near-black surface with a clearly visible light hairline border
// (matching the reference), soft black glow for depth.
function Window({ children, className = "" }) {
    return (<div className={`overflow-hidden rounded-2xl border border-white/[0.12] bg-[#070707] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85)] ${className}`}>
      {children}
    </div>);
}
// Transparent glass container that sits behind/around each window (the frame visible in the
// reference). The window nests at the top with a little margin of glass all around; on desktop
// the callers give it `h-full` + a bottom fade mask so every stage cuts off at one shared
// baseline, letting the section end sooner.
function GlassStage({ children, className = "", solid = false, glowColor = "#33F7BE", }) {
    return (<div className="relative h-full">
  {/* brand-tinted glow behind the card; hidden at rest, breathes only while the .group is hovered (home.css) */}
  <div aria-hidden="true" style={{ backgroundColor: glowColor }} className="animate-breathe-glow pointer-events-none absolute -inset-1 -z-10 rounded-[2rem] blur-[16px]"/>
  <div className={`rounded-[1.75rem] border border-white/[0.06] p-2 backdrop-blur-[2px] transition-colors duration-300 group-hover:border-white/[0.12] ${solid
            ? "bg-[#0a0a0a]"
            : "bg-gradient-to-b from-white/[0.05] to-white/[0.01] group-hover:from-black/70 group-hover:to-black/50"} ${className}`}>
  {children}
  </div>
  </div>);
}
// macOS-style traffic dots.
function TrafficDots() {
    return (<div className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]"/>
      <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]"/>
      <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]"/>
    </div>);
}
/* -------------------- LEFT: Driver Verifications API -------------------- */
function DriverVerificationsWindow() {
    const ACCENT = "text-sky-300"; // rentID blue
    return (<Window className="w-full">
      <div className="flex items-center gap-2 border-b border-white/[0.1] bg-white/[0.02] px-4 py-2.5">
        <TrafficDots />
        <span className="ml-2 text-[11px] font-medium text-white/40">API Response</span>
      </div>
      <pre className="overflow-hidden px-4 py-3.5 font-mono text-[11px] leading-[1.7] text-white/70">
        <span className="text-white/90">POST /api/driver-verifications</span>
        {"\n\n"}
        <span className="text-white/40">HTTP/1.1 200 OK</span>
        {"\n"}
        {"{"}
        {"\n"}
        {'  "driver_id": '}
        <span className="text-white/80">{'"drv_123456"'}</span>,{"\n"}
        {"  "}
        <span className={ACCENT}>{'"verification_check"'}</span>
        {": "}
        <span className={ACCENT}>{'"passed"'}</span>,{"\n"}
        {"  "}
        <span className={ACCENT}>{'"license_status"'}</span>
        {": "}
        <span className={ACCENT}>{'"active"'}</span>,{"\n"}
        {'  "name": '}
        <span className="text-white/80">{'"Taylor Morgan"'}</span>,{"\n"}
        {'  "verified_at": '}
        <span className="text-white/80">{'"2024-09-05T14:22:33Z"'}</span>
        {"\n"}
        {"}"}
        {"\n\n"}
        <span className="text-white/40">HTTP/1.1 200 OK</span>
        {"\n"}
        {"{"}
        {"\n"}
        {'  "driver_id": '}
        <span className="text-white/80">{'"drv_987654"'}</span>,{"\n"}
        {"  "}
        <span className={ACCENT}>{'"verification_check"'}</span>
        {": "}
        <span className={ACCENT}>{'"suspended"'}</span>,{"\n"}
        {"  "}
        <span className={ACCENT}>{'"license_status"'}</span>
        {": "}
        <span className={ACCENT}>{'"suspended"'}</span>,{"\n"}
        {'  "name": '}
        <span className="text-white/80">{'"Jordan Lee"'}</span>,{"\n"}
        {'  "verified_at": '}
        <span className="text-white/80">{'"2024-09-05T10:11:02Z"'}</span>
        {"\n"}
        {"}"}
      </pre>
    </Window>);
}
/* -------------------- CENTER: Rental Brand commerce app -------------------- */
function CommerceWindow() {
    const vehicles = [
        { name: "Toyota RAV4", price: "$46", img: "/images/vehicles/toyota-rav4.webp" },
        { name: "Tesla Model Y", price: "$76", img: "/images/vehicles/tesla-model-y.webp" },
        { name: "Ford F-150", price: "$90", img: "/images/vehicles/ford-f150.webp" },
    ];
    const reservations = [
        { id: "#RB-2041", label: "Confirmed", cls: "bg-emerald-500/15 text-emerald-300" },
        { id: "#RB-2039", label: "Active", cls: "bg-sky-500/15 text-sky-300" },
        { id: "#RB-2036", label: "Pending", cls: "bg-amber-500/15 text-amber-300" },
    ];
    return (<Window className="w-full border-white/[0.14]">
      {/* browser chrome — dark graphite */}
      <div className="flex items-center gap-3 bg-[#101010] px-4 py-2.5">
        <TrafficDots />
        <div className="mx-auto flex items-center gap-1.5 rounded-md bg-white/[0.05] px-3 py-1">
          <Lock aria-hidden="true" className="h-3 w-3 text-white/40"/>
          <span className="font-mono text-[11px] text-white/60">rentalbrand.com</span>
        </div>
      </div>

      {/* site nav */}
      <div className="flex items-center justify-between gap-3 px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-bold text-white">Rental Brand</span>
        </div>
        <nav className="hidden items-center gap-4 text-[10px] text-white/50 md:flex">
          <span>Vehicles</span>
          <span>Locations</span>
          <span>For Business</span>
          <span>Support</span>
        </nav>
        <div className="flex items-center gap-2.5">
          <span className="text-[10px] text-white/50">Sign In</span>
          <span className="rounded-md bg-white px-2.5 py-1 text-[10px] font-semibold text-black">Book a Car</span>
        </div>
      </div>

      {/* hero — larger, richer image area */}
      <div className="relative mx-5 overflow-hidden rounded-xl">
        <div className="relative aspect-[16/8] w-full">
          <Image src="/images/home/commerce-hero-suv.webp" alt="SUV on a scenic coastal road" fill sizes="680px" className="object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/25 to-transparent"/>
          <div className="absolute inset-0 flex flex-col justify-center gap-1.5 px-6">
            <p className="font-heading text-[26px] font-bold leading-[1.02] tracking-tight text-white">
              Move
              <br />
              Without Limits
            </p>
            <p className="max-w-[210px] text-[10px] leading-snug text-white/75">
              Premium vehicles for work, travel, and whatever&apos;s next.
            </p>
          </div>
        </div>
      </div>

      {/* search / booking bar */}
      <div className="mx-5 mt-3.5 flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.025] p-2.5">
        <div className="min-w-0 flex-1">
          <p className="text-[8px] uppercase tracking-wide text-white/35">Pick up</p>
          <p className="truncate text-[10px] text-white/80">San Francisco, CA</p>
        </div>
        <span className="h-6 w-px bg-white/10"/>
        <div className="min-w-0 flex-1">
          <p className="text-[8px] uppercase tracking-wide text-white/35">Dates</p>
          <p className="truncate text-[10px] text-white/80">Sep 5 - Sep 8, 2024</p>
        </div>
        <span className="h-6 w-px bg-white/10"/>
        <div className="min-w-0 flex-1">
          <p className="text-[8px] uppercase tracking-wide text-white/35">Vehicle type</p>
          <p className="truncate text-[10px] text-white/80">All vehicles</p>
        </div>
        <span className="flex items-center gap-1 rounded-lg bg-[#2563eb] px-3 py-2 text-[10px] font-semibold text-white">
          <Search aria-hidden="true" className="h-3 w-3"/>
          Search
        </span>
      </div>

      {/* lower two columns */}
      <div className="grid grid-cols-2 gap-4 px-5 pb-5 pt-4">
        <div>
          <p className="mb-2.5 text-[10px] font-semibold text-white/80">Featured Vehicles</p>
          <div className="flex flex-col gap-2">
            {vehicles.map((v) => (<div key={v.name} className="flex items-center gap-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-2">
                <span className="relative h-9 w-14 shrink-0 overflow-hidden rounded-md bg-black/40">
                  <Image src={v.img || "/placeholder.svg"} alt={v.name} fill sizes="56px" className="object-cover"/>
                </span>
                <span className="truncate text-[10px] text-white/80">{v.name}</span>
                <span className="ml-auto whitespace-nowrap text-[10px] font-semibold text-white">
                  {v.price}
                  <span className="text-white/40">/day</span>
                </span>
              </div>))}
          </div>
        </div>
        <div>
          <p className="mb-2.5 text-[10px] font-semibold text-white/80">Reservations</p>
          <div className="flex flex-col gap-2">
            {reservations.map((r) => (<div key={r.id} className="flex items-center justify-between gap-2 rounded-lg border border-white/[0.05] bg-white/[0.02] p-2">
                <span className="font-mono text-[10px] text-white/60">{r.id}</span>
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${r.cls}`}>{r.label}</span>
              </div>))}
          </div>
        </div>
      </div>
    </Window>);
}
/* -------------------- RIGHT: Commercial Insurance API -------------------- */
function InsuranceWindow() {
    const ACCENT = "text-[#fb7185]"; // rentShield coral
    return (<Window className="w-full">
      <div className="flex items-center gap-2 border-b border-white/[0.1] bg-white/[0.02] px-4 py-2.5">
        <TrafficDots />
        <span className="ml-2 text-[11px] font-medium text-white/40">Terminal</span>
      </div>
      <pre className="overflow-hidden px-4 py-3 font-mono text-[11px] leading-[1.7] text-white/70">
        <span className="text-white/90">
          POST <span className={ACCENT}>/api/policies/activate</span>
        </span>
        {"   "}
        <span className="text-white/40">HTTP/1.1 200 OK</span>
        {"\n"}
        {"{ "}
        <span className="text-white/80">{'"policy_id": "pol_8f3c2e1"'}</span>,{"\n"}
        {"  "}
        <span className={ACCENT}>{'"policy_type"'}</span>
        {": "}
        <span className={ACCENT}>{'"liability policy"'}</span>,{" "}
        <span className={ACCENT}>{'"status"'}</span>
        {": "}
        <span className={ACCENT}>{'"activated"'}</span>,{"\n"}
        {"  "}
        <span className={ACCENT}>{'"coverage"'}</span>
        {": "}
        <span className={ACCENT}>{'"$1,000,000"'}</span>,{" "}
        {'"customer_id": '}
        <span className="text-white/80">{'"cus_456789"'}</span>
        {"\n"}
        {"}"}
        {"\n\n"}
        <span className="text-white/90">
          POST <span className={ACCENT}>/api/policies/activate</span>
        </span>
        {"   "}
        <span className="text-white/40">HTTP/1.1 200 OK</span>
        {"\n"}
        {"{ "}
        <span className="text-white/80">{'"policy_id": "pol_5b91a7d"'}</span>,{"\n"}
        {"  "}
        <span className={ACCENT}>{'"policy_type"'}</span>
        {": "}
        <span className={ACCENT}>{'"collision policy"'}</span>,{" "}
        <span className={ACCENT}>{'"status"'}</span>
        {": "}
        <span className={ACCENT}>{'"activated"'}</span>,{"\n"}
        {"  "}
        <span className={ACCENT}>{'"coverage"'}</span>
        {": "}
        <span className={ACCENT}>{'"$750,000"'}</span>,{" "}
        {'"vehicle_id": '}
        <span className="text-white/80">{'"veh_321654"'}</span>,{"\n"}
        {"  "}
        <span className={ACCENT}>{'"start_date"'}</span>
        {": "}
        <span className={ACCENT}>{'"2026-09-05"'}</span>,{" "}
        <span className={ACCENT}>{'"end_date"'}</span>
        {": "}
        <span className={ACCENT}>{'"2026-09-08"'}</span>
        {"\n"}
        {"}"}
      </pre>
    </Window>);
}
/* Suite brand mark — just the full-color logo, no tile, glass, border, or label. When its card
   is hovered (`group-hover`, the group is the card) a soft brand-tinted glow blooms on the mark
   to tie it to the beam that runs into it. */
function SuiteBadge({ src, alt, glow, size = 88, className = "", }) {
    return (<span className={`block ${className}`} style={{ height: size, width: size, ["--glow"]: glow }}>
      <Image src={src} alt={alt} fill sizes="128px" className="object-contain transition-[filter] duration-300 group-hover:[filter:drop-shadow(0_0_14px_var(--glow))]"/>
    </span>);
}
// RentDirect mark — the play-mark on the commerce app.
function RentalBrandBadge({ className = "" }) {
    return (<SuiteBadge src="/images/rentid-suite/rentdirect-mark.png" alt="RentDirect" glow="rgba(16,185,129,0.55)" size={36} className={className}/>);
}
// RentShield mark — coral shield.
function RentShieldBadge({ className = "" }) {
    return (<SuiteBadge src="/images/rentid-suite/rentshield-mark.png" alt="RentShield" glow="rgba(245,44,82,0.6)" size={46} className={className}/>);
}
// RentID mark — the "iD" verification mark, paired with the Driver Verifications window.
function RentIdBadge({ className = "" }) {
    return (<SuiteBadge src="/images/rentid-suite/rentid-mark.png" alt="RentID" glow="rgba(56,189,248,0.6)" size={44} className={className}/>);
}
export function HomeInfrastructureShowcase() {
    const inshur = useInshurLanding(); // /inshur: no off-page buttons
    return (<section aria-labelledby="rentid-infra-heading" className="relative w-full rounded-t-[2.5rem] bg-black px-5 py-16 sm:px-8 sm:py-20 lg:rounded-t-[3rem] md:px-16 lg:py-24">
      <div className="mx-auto w-full max-w-7xl">
      {/* Header — mirrors RentidEnterpriseSection, dark theme. */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs font-bold uppercase tracking-widest text-card">Rentid enterprise</p>
        {!inshur && (<a href={LINKS.enterprise} className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-black transition-transform duration-200 hover:scale-[1.02] active:scale-95">
          API Access
        </a>)}
      </div>
      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16">
        <h2 id="rentid-infra-heading" className="font-heading text-3xl font-light leading-[1.05] tracking-tight text-balance text-[#33F7BE] sm:text-4xl md:text-[42px]">
          Enterprise level APIs to build your platform
        </h2>
        <div className="flex items-center lg:justify-end">
          <p className="max-w-md text-lg leading-relaxed text-pretty text-card lg:text-right">
            Technology partners can gain access to our APIs for bookings, verifications, and insurance.
          </p>
        </div>
      </div>

      {/* Cinematic hero panel — flat #1C1C1C to match the rentID card, with a whisper of
            charcoal topography. The darker #070707 windows pop against this surface for depth. */}
      <div className="relative mt-12 overflow-hidden rounded-[2rem] border border-white/[0.1] bg-[#1C1C1C]">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <Image src="/images/home/enterprise-circuit-bg.webp" alt="" fill sizes="100vw" className="object-cover opacity-[0.02] grayscale"/>
        </div>
        {/* very soft vignette for depth (kept light so the surface still reads as flat #1C1C1C) */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_55%,rgba(0,0,0,0.35)_100%)]"/>

        {/* Artwork stage. Mobile: stacked. Desktop (lg): absolute overlapping composition. */}
        <div className="relative">
          {/* Mobile / tablet fallback — stacked, no overlaps. */}
          <div className="flex flex-col gap-8 p-6 sm:p-10 lg:hidden">
            <div className="group">
              <FloatingLabel label="Driver Verifications API" className="mb-3"/>
              <GlassStage glowColor="#38BDF8">
                <DriverVerificationsWindow />
              </GlassStage>
            </div>
            <div className="group">
              <FloatingLabel label="Customizable commerce app" className="mb-3"/>
              <GlassStage>
                <CommerceWindow />
              </GlassStage>
            </div>
            <div className="group">
              <FloatingLabel label="Commercial Insurance API" className="mb-3"/>
              <GlassStage glowColor="#fb7185">
                <InsuranceWindow />
              </GlassStage>
            </div>
          </div>

          {/* Desktop composition — dominant center, API windows layered around it. Every stage
            shares the SAME bottom (bottom-[7%]) so all three glass containers cut off at one
            baseline; a bottom fade mask on each makes them dissolve there, letting the panel
            end much sooner than the tall center app would otherwise force. Staggered tops keep
            the layered look. Layering (back -> front): left verification, center commerce,
            right insurance, labels, brand badges. */}
          <div className="relative mx-auto hidden h-[620px] w-full max-w-[1280px] lg:block">
            {/* left verification window (behind center). `group` so hovering the card lights
            the RentID badge and reveals the beam running into it. */}
            <div className="group absolute bottom-[22%] left-[5%] top-[34%] z-10 w-[372px]">
              <FloatingLabel label="Driver Verifications API" className="absolute -top-[58px] left-0"/>
              <GlassStage glowColor="#38BDF8" className="h-full overflow-hidden [mask-image:linear-gradient(to_bottom,black_82%,transparent_100%)]">
                <DriverVerificationsWindow />
              </GlassStage>
              {/* Centered on the frame's top edge, and centered in the gap between the label's right
                  edge and the commerce app's frame. */}
              <RentIdBadge className="absolute -top-[22px] left-[242px] z-40"/>
            </div>

            {/* right insurance window (in front of center) — extended so it reaches the shared
            baseline. `group` lights the RentShield badge + beam on hover; label moved LEFT
            to clear the top-right badge. */}
            <div className="group absolute bottom-[7%] left-[50%] top-[46%] z-30 w-[520px] translate-x-[50px]">
              <FloatingLabel label="Commercial Insurance API" className="absolute -top-[58px] right-0"/>
              <GlassStage glowColor="#fb7185" className="h-full overflow-hidden [mask-image:linear-gradient(to_bottom,black_82%,transparent_100%)]">
                <InsuranceWindow />
              </GlassStage>
              {/* Sits where the terminal's title-bar separator meets the right edge of the card. */}
              <RentShieldBadge className="absolute -right-6 top-[24px] z-40"/>
            </div>

            {/* center commerce window (dominant focal point) — cut off at the shared baseline so
            it no longer stretches the section. `group` lights the RentDirect badge + beam on
            hover; label moved LEFT to clear the top-right badge. */}
            <div className="group absolute bottom-[16%] left-[26.5%] top-[16%] z-20 w-[632px] translate-x-[50px] translate-y-[15px]">
              <FloatingLabel label="Customizable commerce app" className="absolute -top-[58px] left-1/2 z-40 -translate-x-1/2"/>
              <GlassStage className="h-full overflow-hidden [mask-image:linear-gradient(to_bottom,black_82%,transparent_100%)]">
                <CommerceWindow />
              </GlassStage>
              {/* Centered on the frame's top edge like the RentID mark, and centered in the gap
                  between the label's right edge and the "Book a Car" button. */}
              <RentalBrandBadge className="absolute -top-[18px] left-[464px] z-40"/>
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>);
}
